import axios from 'axios';
import {ENDPOINT_URL, GET_PLAN} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a guest access plan row identifier.
 * @param {object} item Canvas plan row
 * @return {*} identifier
 */
function guestPlanId(item) {
	return item.id ?? item.plan_id;
}

/**
 * Normalize a plan row into an agent candidate.
 *
 * The name is the Plan Name column, which is what the table shows first and
 * what a request names. The rest travel as `field_values` keyed by the
 * catalogue's own field ids, because the candidate shape forbids extras.
 *
 * @param {object} item Canvas plan row
 * @return {object|null} agent candidate
 */
function normalizeGuestPlan(item) {
	const id = guestPlanId(item);
	const name = item.plan_name ?? item.name;
	if (id == null || !name) return null;
	const values = {plan_name: String(name)};
	if (item.description) values.plan_description = String(item.description);
	if (item.total_quota != null) {
		values.total_quota = String(item.total_quota);
	}
	if (item.upload_quota != null) {
		values.upload_quota = String(item.upload_quota);
	}
	if (item.download_quota != null) {
		values.download_quota = String(item.download_quota);
	}
	return {
		entity_type: 'guest_plan',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
		field_values: values,
	};
}

// Words that mean no plan was actually named - "edit the guest plan" with
// nothing left over. Unfiltered, a bare "plan" is a valid substring of every
// row's name and resolves to a target nobody chose. The same guard the
// captive portal resolver needs, for the same reason.
const GENERIC_QUERIES = new Set([
	'guest access plan', 'guest access plans', 'guest plan', 'guest plans',
	'access plan', 'access plans', 'plan', 'plans',
	'a plan', 'the plan',
]);

/**
 * Resolve guest access plan candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveGuestPlans(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (GENERIC_QUERIES.has(query.toLowerCase())) {
		throw new Error('no_target_specified');
	}
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_PLAN}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('guest_plan', rows, guestPlanId);
		return {
			candidates: rows
				.map(normalizeGuestPlan)
				.filter(Boolean)
				.slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeGuestPlan,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('guest_plan', rows, guestPlanId);
	return {candidates};
}

/**
 * Return a retained guest access plan row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedGuestPlan(entityId) {
	return getResolvedEntity('guest_plan', entityId);
}
